import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Party } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PoliticalMap2D, PoliticalMapLegend, type MapEntity } from '@/components/ideology/PoliticalMap2D'

// ============================================================================
// Types
// ============================================================================

interface NationPoliticalLandscapeProps {
  parties: Party[]
  myPartyId?: string
  isLoading?: boolean
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function NationPoliticalLandscape({
  parties,
  myPartyId,
  isLoading,
  className,
}: NationPoliticalLandscapeProps) {
  const navigate = useNavigate()
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  // Convert parties to map entities
  const mapEntities: MapEntity[] = useMemo(() => {
    return parties.map(party => ({
      id: party.id,
      name: party.name,
      ideology: party.ideology_vector,
      color: party.color,
      type: 'party' as const,
    }))
  }, [parties])

  const handleEntityClick = (id: string) => {
    navigate(`/parties/${id}`)
  }

  const handleEntityHover = (id: string | null) => {
    setHighlightedId(id)
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Political Landscape</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            <div className="bg-muted h-[400px] w-[400px] animate-pulse rounded" />
            <div className="flex-1 space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-muted h-8 w-full animate-pulse rounded" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (parties.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Political Landscape</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-center py-8">
            No parties have formed yet. Be the first to shape this nation's political landscape!
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Political Landscape</CardTitle>
        <p className="text-sm text-muted-foreground">
          See where parties stand on the political compass. Click a party to view their profile.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Map */}
          <div className="flex-shrink-0">
            <PoliticalMap2D
              entities={mapEntities}
              width={400}
              height={400}
              showGridLabels={true}
              showSubLabels={true}
              showAxisLabels={true}
              onEntityClick={handleEntityClick}
              onEntityHover={handleEntityHover}
              highlightedId={highlightedId || myPartyId}
            />
          </div>

          {/* Legend */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Parties ({parties.length})
            </h4>
            <PoliticalMapLegend
              entities={mapEntities}
              onEntityClick={handleEntityClick}
              onEntityHover={handleEntityHover}
              highlightedId={highlightedId || myPartyId}
            />

            {/* Map explanation */}
            <div className="mt-6 pt-4 border-t text-xs text-muted-foreground space-y-2">
              <div>
                <strong>Horizontal axis (Left to Right):</strong> Economic policy and social values.
                Left favors regulation and progressive values; Right favors markets and traditional values.
              </div>
              <div>
                <strong>Vertical axis (Pluralist to Centralized):</strong> Government structure preferences.
                Pluralist favors decentralized, diverse power; Centralized favors strong unified authority.
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
